import { Shield, X, Eye, Menu } from 'lucide-react';
import { useAdmin } from '../lib/AdminContext';
import { useApp } from '../lib/AppContext';

interface AdminHeaderProps {
  onMenuToggle?: () => void;
}

export default function AdminHeader({ onMenuToggle }: AdminHeaderProps) {
  const { viewAsUser, toggleViewAsUser, exitAdminMode, previousRoute, previousCardId, enterAdminMode } = useAdmin();
  const { navigateTo, currentScreen, currentCardId } = useApp();

  const handleExit = () => {
    exitAdminMode();
    navigateTo(previousRoute || 'profile');
  };

  const handlePreviewToggle = () => {
    if (!viewAsUser) {
      // Entering preview mode - save current position
      console.log('[AdminHeader] Entering preview mode, current position:', { currentScreen, currentCardId });

      // Save to localStorage directly
      if (currentScreen) {
        localStorage.setItem('civicbeat_previous_route', currentScreen);
      }
      if (currentCardId) {
        localStorage.setItem('civicbeat_previous_card_id', currentCardId);
      } else {
        localStorage.removeItem('civicbeat_previous_card_id');
      }

      enterAdminMode(currentScreen, currentCardId || undefined);
      toggleViewAsUser();
      navigateTo('home');
    } else {
      // Exiting preview mode - restore previous position
      const savedRoute = localStorage.getItem('civicbeat_previous_route');
      const savedCardId = localStorage.getItem('civicbeat_previous_card_id');

      console.log('[AdminHeader] Exiting preview mode, restoring:', { savedRoute, savedCardId, previousRoute, previousCardId });

      toggleViewAsUser();

      if (savedRoute) {
        navigateTo(savedRoute as any, savedCardId || undefined);
      } else if (previousRoute) {
        navigateTo(previousRoute as any, previousCardId || undefined);
      }
    }
  };

  return (
    <header className="h-16 border-b border-[#282828] bg-[#111111] flex items-center justify-between px-4 lg:px-6">
      <div className="flex items-center gap-3">
        {/* Mobile Menu Button */}
        <button
          onClick={onMenuToggle}
          className="lg:hidden p-2 rounded-lg hover:bg-[#1A1A1A] text-[#A0A0A0] transition-colors"
        >
          <Menu className="w-5 h-5" strokeWidth={2} />
        </button>

        <Shield className="w-5 h-5 text-[#FFB86C]" strokeWidth={2} />
        <h1 className="text-[14px] lg:text-[16px] font-semibold text-[#F5F5F5]">
          Admin Workspace
        </h1>
      </div>

      <div className="flex items-center gap-2 lg:gap-3">
        {/* Mobile: Icon-only Preview button */}
        <button
          onClick={handlePreviewToggle}
          aria-label="Preview as User"
          title="Preview as User"
          className={`inline-flex md:hidden items-center justify-center p-2 rounded-lg border transition-all ${
            viewAsUser
              ? 'bg-[#40E0D0]/10 border-[#40E0D0]/30 text-[#40E0D0]'
              : 'bg-[#181818] border-[#282828] text-[#A0A0A0] hover:bg-[#282828] hover:text-[#F5F5F5]'
          } focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#40E0D0] focus-visible:outline-offset-2`}
        >
          <Eye className="w-4 h-4" strokeWidth={2} />
        </button>

        {/* Desktop: Full Preview button */}
        <button
          onClick={handlePreviewToggle}
          className={`hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
            viewAsUser
              ? 'bg-[#40E0D0]/10 border-[#40E0D0]/30 text-[#40E0D0]'
              : 'bg-[#181818] border-[#282828] text-[#A0A0A0] hover:bg-[#282828] hover:text-[#F5F5F5]'
          } focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#40E0D0] focus-visible:outline-offset-2`}
        >
          <Eye className="w-4 h-4" strokeWidth={2} />
          <span className="text-[13px] font-medium">Preview as User</span>
        </button>

        <button
          onClick={handleExit}
          className="flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg bg-[#181818] hover:bg-[#282828] border border-[#282828] text-[#F5F5F5] text-[13px] font-medium transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#40E0D0] focus-visible:outline-offset-2"
        >
          <X className="w-4 h-4" strokeWidth={2} />
          <span className="hidden sm:inline">Exit Admin</span>
          <span className="sm:hidden">Exit</span>
        </button>
      </div>
    </header>
  );
}
